import { AsyncPipe } from '@angular/common';
import { Component, OnInit, inject, input, output } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import {
  NgbDropdown,
  NgbDropdownToggle,
  NgbDropdownMenu,
  NgbDropdownButtonItem,
  NgbDropdownItem,
} from '@ng-bootstrap/ng-bootstrap';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { AppName } from '../../constants';
import {
  FormlyButtonDropdown,
  FormlyButtonDropdownItem,
  GridPreviousNext,
  SummaryButton,
  SummaryItem,
} from '../../models';
import { MenuService, ModalService, RouterService } from '../../services';
import { FormService } from '../../services/component-services/form.service';
import { DiscardChangesModalComponent } from '../modals/discard-changes-modal/discard-changes-modal.component';
import { BaseComponent } from '../base/base.component';
import { BackButtonComponent } from '../back-button/back-button.component';
import { SummaryComponent } from '../summary/summary.component';
import { ignoreModalError } from '../../helpers';

@Component({
  selector: 'app-summary-top',
  templateUrl: './summary-top.component.html',
  styleUrl: './summary-top.component.scss',
  imports: [
    BackButtonComponent,
    SummaryComponent,
    NgbDropdown,
    NgbDropdownToggle,
    NgbDropdownMenu,
    NgbDropdownButtonItem,
    NgbDropdownItem,
    AsyncPipe,
  ],
})
export class SummaryTopComponent extends BaseComponent implements OnInit {
  protected readonly menuService = inject(MenuService);
  private readonly formService = inject(FormService);
  private readonly routerService = inject(RouterService);
  private readonly modalService = inject(ModalService);
  private readonly route = inject(ActivatedRoute);
  private readonly title = inject(Title);

  public readonly header = input<string>();
  public readonly name = input<string>();
  readonly model$ = input<Observable<any>>();
  public readonly additionalButtons = input<(SummaryButton | FormlyButtonDropdown)[]>([]);
  public readonly alwaysShowAdditionalButtons = input(false);
  public readonly summaryItems = input<(model: any) => SummaryItem[]>();
  public readonly fullWidth = input(false);
  public readonly mobile = input(false);
  public readonly showBack = input(true);
  public readonly showCopy = input(false);
  public readonly showSave = input(true);
  public readonly saveButtonText = input($localize`Save`);
  public readonly copyButtonText = input($localize`Copy`);
  public readonly enableCopyButton = input(true);
  public readonly overrideSave = input(false);
  public readonly copyModel = output<void>();
  public readonly saveClicked = output<void>();

  protected readonly createHeader = $localize`Create`;
  protected readonly editHeader = $localize`Edit`;

  private modalLevel: number;
  private saved = false;

  protected ids: GridPreviousNext = {} as GridPreviousNext;

  ngOnInit(): void {
    this.modalLevel = this.modalService.modalLevel;
    this.formService.saveComplete.pipe(takeUntil(this.destroy$)).subscribe(() => (this.saved = true));
    if (!this.isModal) {
      this.ids = this.routerService.previousNextIds();
    }

    if (this.header() || this.name()) {
      this.title.setTitle(`${AppName.tmsWeb} - ${this.header() ?? this.name()}`);
    }
  }

  get isModal() {
    return this.modalLevel > 0;
  }

  get showSaveButton() {
    return this.showSave() && !this.menuService.isReadOnly;
  }

  showAdditionalButtons(model: any) {
    return (
      !!this.additionalButtons()?.length &&
      !this.menuService.isReadOnly &&
      (this.alwaysShowAdditionalButtons() || !this.isNewModel(model))
    );
  }

  showCopyButton(model: any) {
    return this.showCopy() && !this.menuService.isReadOnly && !this.isNewModel(model);
  }

  isNewModel(model: any): boolean {
    return this.formService.isNewModel(model);
  }

  dropdownItems(button: SummaryButton | FormlyButtonDropdown): FormlyButtonDropdownItem[] {
    return this.isDropdown(button) ? button.items : [];
  }

  isDropdown(button: SummaryButton | FormlyButtonDropdown): button is FormlyButtonDropdown {
    return !!button && typeof button === 'object' && 'items' in button && Array.isArray((button as any).items);
  }

  dropdownLabel(button: SummaryButton | FormlyButtonDropdown): string {
    if (!this.isDropdown(button)) return '';
    return button.items?.[0]?.text ?? 'Actions';
  }

  dropdownIconClass(button: SummaryButton | FormlyButtonDropdown): string {
    if (!this.isDropdown(button)) return '';
    return button.items?.[0]?.iconClass ?? 'info';
  }

  dropdownMenuItems(button: SummaryButton | FormlyButtonDropdown): FormlyButtonDropdownItem[] {
    if (!this.isDropdown(button)) return [];
    return button.items?.slice(1) ?? [];
  }

  onDropdownItemClick(item: FormlyButtonDropdownItem, model: any) {
    if (item.onClick) {
      item.onClick();
    } else if (item.select) {
      item.select(model);
    }
  }

  back() {
    if (this.formService.anyFormChanges(this.modalLevel)) {
      this.modalService
        .openMedium<DiscardChangesModalComponent>(DiscardChangesModalComponent)
        .modal.result.then((discardChanges: boolean) => {
          if (discardChanges) {
            this.formService.resetForms(this.modalService.modalLevel);
            this.navigate();
          }
        }, ignoreModalError);
    } else {
      this.navigate();
    }
  }

  previous() {
    this.routerService.edit(this.ids.previousId, this.route);
  }

  next() {
    this.routerService.edit(this.ids.nextId, this.route);
  }

  save() {
    this.saveClicked.emit();

    if (this.overrideSave()) {
      return;
    }

    this.formService.save.next(this.modalLevel);
  }

  private navigate() {
    if (this.isModal) {
      if (this.saved) {
        this.modalService.close();
      } else {
        this.modalService.dismiss();
      }
    } else if (this.mobile()) {
      this.routerService.top();
    } else {
      this.routerService.back(this.route);
    }
  }
}
